/**
 * SUSI (Super User-friendly Slider Implemention) is a minimal, responsive slider.
 * Used in conjunction with styles/objects/_slider.en-susi.scss
 *
 * Required markup (class and tag names may vary):
 * ```
 * <div class="en-susi">
 *     <ul class="en-susitems">
 *         <li class="en-susitem"></li>
 *         <li class="en-susitem"></li>
 *         <li class="en-susitem"></li>
 *     </ul>
 * </div>
 * ```
 * Implementation example:
 * ```
 * var myLittleSusi = new EN.Susi($('.en-susi'), {
 *     bullets: true,
 *     cssMethod: Modernizr.csstransforms ? 'transform' : 'left'
 * });
 * ```
 *
 * @class Susi
 * @author EN GARDE (Martin Holler, Marcel Seelig)
 * @version 1.6.1
 *
 * @param {jQuery} $element - root element (e.g. $('.en-susi'))
 * @param {object} [options] - Overrides defaults, see below.
 * @property {boolean} arrows=true - Generates navigation arrows if they don't already exist.
 * @property {boolean} bullets=true - Generates navigation bullets if they don't already exist; the attribute `data-en-susitem-bullet-text` is used as a descriptive text if it is set on the item.
 * @property {boolean} loop=true - Jumps to the first item when the last one is reached and vice versa.
 * @property {number} autoPlay - Moves to the next item in a given interval, stops on user interaction.
 * @property {number} startIndex=0 - (Zero-based) index of the item to be shown first.
 * @property {number|string} itemsPerSlide=1 - How many items there are in one slide. Setting this to "auto" will calculate the value based on the width of the items and automatically update on resize.
 * @property {boolean} swipeSupport=true - Whether or not Susi supports swipe gestures on mobile devices (requires touchSwipe.js).
 * @property {boolean} keySupport=true - Whether or not left and right arrow keys can be used to navigate. Note that this is only possible if Susi has focus, e.g. if it was already interacted with.
 * @property {string} cssMethod=transform - Defines which css property is set.
 * @property {function} callback - A function to be called after every slide move; receives the current index.
 * @property {object} classNames - Collection of class names that are set by Susi.
 */

var EN = EN || {};

EN.Susi = (function (_, $) {
	'use strict';

	/** @constructor */
	function Susi($element, options) {
		var that = this;

		if (!$element || !($element instanceof jQuery)) {
			throw new Error('Susi says: Please supply a valid jQuery element.');
		}

		that.options = $.extend(true, {}, that.defaults, options);

		// bail if the element was already initialized
		// @todo debug this issue further
		// if ($element.hasClass(that.options.classNames.initialized)) {
		// 	return false;
		// }

		that.index = that.options.startIndex;
		that.$element = $element.first();
		that.$container = that.$element.find('.' + that.options.classNames.items);

		$(function () {
			that.init();

			if ($element.hasClass(that.options.classNames.initialized)) {
				that.goToItem(that.index, true);
			}
		});
	}

	// defaults
	Susi.prototype.defaults = {
		arrows: true,
		bullets: false,
		loop: true,
		autoPlay: false,
		autoPlayInterval: 6000,
		startIndex: 0,
		itemsPerSlide: 1,
		swipeSupport: true,
		keySupport: true,
		cssMethod: 'transform',
		callback: false,
		classNames: {
			items: 'en-susitems',
			bullets: 'en-susi__bullets',
			bullet: 'en-susi__bullet',
			bulletText: 'en-susi__bullet-text',
			arrows: 'en-susi__arrows',
			arrow: 'en-susi__arrow',
			arrowPrev: 'en-susi__arrow--prev',
			arrowNext: 'en-susi__arrow--next',
			active: 'en-is-active',
			disabled: 'en-is-disabled',
			initialized: 'en-is-initialized'
		}
	};

	/**
	 * Generates navigation elements and sets up event listeners.
	 * This method can be called multiple times to add/remove items at runtime.
	 * @memberof Susi
	 * @return {jQuery} $element
	 */
	Susi.prototype.init = function () {
		var that = this;
		var insertString = '';

		// bail if there are not enough elements
		if (that.$container.children().length <= that.getItemsPerSlide()) {
			return that.$element;
		}

		// create arrows (if there no arrows yet)
		if (that.options.arrows && !that.$element.find('.' + that.options.classNames.arrows).length) {
			insertString += '<div class="' + that.options.classNames.arrows + '">';

			// prev arrow
			insertString += '<span class="' + that.options.classNames.arrow + ' ' + that.options.classNames.arrowPrev + '" />';

			// next arrow
			insertString += '<span class="' + that.options.classNames.arrow + ' ' + that.options.classNames.arrowNext + '" />';

			insertString += '</div>';
		}

		// create bullets (if there no bullets yet)
		if (that.options.bullets && !that.$element.find('.' + that.options.classNames.bullets).length) {
			insertString += '<ul class="' + that.options.classNames.bullets + '">';

			that.$container.children().each(function (index) {
				insertString += '<li class="' + that.options.classNames.bullet + '">';
				insertString += '<span class="' + that.options.classNames.bulletText + '">' + ($(this).attr('data-en-susitem-bullet-text') || '') + '</span>';
				insertString += '</li>';
			});

			insertString += '</ul>';
		}

		if (that.options.arrows || that.options.bullets) {
			that.$element.append($(insertString));
		}

		// cache arrows & bullets
		that.$arrowPrev = that.$element.find('.' + that.options.classNames.arrowPrev);
		that.$arrowNext = that.$element.find('.' + that.options.classNames.arrowNext);
		that.$bullets = that.$element.find('.' + that.options.classNames.bullets);

		that.updateClasses();

		// event listeners setup
		that.$element.off('click', '.' + that.options.classNames.arrowPrev).on('click', '.' + that.options.classNames.arrowPrev, function () {
			that.pause();
			that.prev();
		});

		that.$element.off('click', '.' + that.options.classNames.arrowNext).on('click', '.' + that.options.classNames.arrowNext, function () {
			that.pause();
			that.next();
		});

		if (that.$bullets.length) {
			that.$element.off('click', '.' + that.options.classNames.bullet).on('click', '.' + that.options.classNames.bullet, function () {
				that.pause();
				that.goToItem($(this).index());
			});
		}

		// autoplay init
		if (that.options.autoPlay) {
			that.play();
		}

		// swipe init (can be applied multiple times)
		if (that.options.swipeSupport && $.fn.swipe) {
			that.$element.swipe({
				swipeLeft: function () {
					that.next();
				},
				swipeRight: function () {
					that.prev();
				}
			});
		}

		// key control init
		if (that.options.keySupport) {
			// set the tabindex so that the element can have focus
			that.$element.attr('tabindex', -1);

			that.$element.off('keydown.Susi').on('keydown.Susi', function (e) {
				if (e.keyCode === 37) {
					that.prev();
				}

				if (e.keyCode === 39) {
					that.next();
				}
			});
		}

		// if itemsPerSlide is set to 'auto', go to the current item after a window resize
		if (that.options.itemsPerSlide === 'auto') {
			$(window).on('resize', _.debounce(function () {
				that.goToItem(that.index);
			}, 200));
		}

		that.$element.addClass(that.options.classNames.initialized);
	};

	/**
	 * Updates control and item classes.
	 * @memberof Susi
	 * @return {jQuery} $element
	 */
	Susi.prototype.updateClasses = function () {
		var that = this;
		var itemsPerSlide = that.getItemsPerSlide();

		// items
		that.$container.children().removeClass(that.options.classNames.active).eq(that.index).addClass(that.options.classNames.active);

		// arrows
		if (that.$arrowPrev.length && that.$arrowNext.length) {
			if (!that.options.loop) {
				that.$arrowPrev.removeClass(that.options.classNames.disabled);
				that.$arrowNext.removeClass(that.options.classNames.disabled);

				if (that.index === 0) {
					that.$arrowPrev.addClass(that.options.classNames.disabled);
				}

				if (that.index === that.$container.children().length - itemsPerSlide) {
					that.$arrowNext.addClass(that.options.classNames.disabled);
				}
			}
			else {
				if (that.$container.children().length <= itemsPerSlide) {
					that.$arrowPrev.addClass(that.options.classNames.disabled);
					that.$arrowNext.addClass(that.options.classNames.disabled);
				}
				else {
					that.$arrowPrev.removeClass(that.options.classNames.disabled);
					that.$arrowNext.removeClass(that.options.classNames.disabled);
				}
			}
		}

		// bullets
		if (that.$bullets.length) {
			that.$bullets.children().removeClass(that.options.classNames.active + ' ' + that.options.classNames.disabled).eq(that.index).addClass(that.options.classNames.active);

			that.$bullets.children().eq(that.$container.children().length - itemsPerSlide).nextAll().addClass(that.options.classNames.disabled);
		}

		return that.$element;
	};

	/**
	 * Returns the items per slide. Calculates the value if the itemsPerSlide option is set to "auto".
	 * @memberof Susi
	 * @return {number}
	 */
	Susi.prototype.getItemsPerSlide = function () {
		var that = this;

		if (that.options.itemsPerSlide === 'auto') {
			return Math.round(that.$container.outerWidth() / that.$container.children().first().outerWidth());
		}
		else {
			return that.options.itemsPerSlide;
		}
	};

	/**
	 * Moves to a given item.
	 * @memberof Susi
	 * @param {number} index
	 * @param {boolean} [noTransition] - If true, transitions will be disabled for this move (transitions are set entirely in css).
	 * @return {jQuery} $element
	 */
	Susi.prototype.goToItem = function (index, noTransition) {
		var that = this;
		var cssProperties = {};
		var firstItemInLastSlide, translateValue;
		var itemsPerSlide = that.getItemsPerSlide();

		if (typeof index !== 'number') {
			throw new Error('Susi says: Invalid index.');
		}

		// correct out of range calls
		if (index < 0) {
			index = 0;
		}
		else if (index > that.$container.children().length - 1) {
			index = that.$container.children().length - 1;
		}

		// get the index of the first item in the last slide
		// this is to avoid blank areas when using multiple items per slide
		firstItemInLastSlide = that.$container.children().length - itemsPerSlide;

		if (firstItemInLastSlide < 0) {
			firstItemInLastSlide = 0;
		}

		// set the actual index
		that.index = index > firstItemInLastSlide ? firstItemInLastSlide : index;

		// set the css property
		translateValue = (-that.index * 100) / itemsPerSlide;

		if (that.options.cssMethod === 'transform') {
			cssProperties.transform = 'translateX(' + translateValue + '%)';
		}
		else {
			cssProperties.left = translateValue + '%';
		}

		cssProperties.transition = noTransition ? 'none' : '';

		that.$container.css(cssProperties);

		that.updateClasses();

		// focus the element for key support
		if (that.options.keySupport && !noTransition && !that.options.autoPlay) {
			that.$element.focus();
		}

		// callback
		if (!noTransition && typeof that.options.callback === 'function') {
			that.options.callback(that.index);
		}

		// fire event
		if (!noTransition) {
			that.$container.children().eq(that.index).trigger('susiupdate');
		}

		return that.$element;
	};

	/**
	 * Moves to the previous item.
	 * @memberof Susi
	 * @return {jQuery} $element
	 */
	Susi.prototype.prev = function () {
		var that = this;
		var itemsPerSlide = that.getItemsPerSlide();
		var index = that.index - itemsPerSlide;

		if (that.options.loop && that.index === 0) {
			index = that.$container.children().length - itemsPerSlide;
		}

		that.goToItem(index);

		return that.$element;
	};

	/**
	 * Moves to the next item.
	 * @memberof Susi
	 * @return {jQuery} $element
	 */
	Susi.prototype.next = function () {
		var that = this;
		var itemsPerSlide = that.getItemsPerSlide();
		var index = that.index + itemsPerSlide;

		if (that.options.loop && that.index === that.$container.children().length - itemsPerSlide) {
			index = 0;
		}

		that.goToItem(index);

		return that.$element;
	};

	/**
	 * Starts autoplay.
	 * @memberof Susi
	 */
	Susi.prototype.play = function () {
		var that = this;
		var timer = that.$element.data('timer');

		if (!timer) {
			timer = setInterval(function () {
				that.next();
			}, that.options.autoPlayInterval);

			that.$element.data('timer', timer);
		}
	};

	/**
	 * Pauses autoplay.
	 * @memberof Susi
	 */
	Susi.prototype.pause = function () {
		var that = this;
		var timer = that.$element.data('timer');

		clearInterval(timer);
		timer = null;

		that.$element.data('timer', timer);
	};

	return Susi;
}(lodash, jQuery));
